import React, { useEffect, useState, useMemo } from 'react';
import PageHeader from '../components/PageHeader';
import '../components/CRDashboard.css';
import { get } from '../api/client';

const CATEGORIES = [
  {
    id: 'attendance', title: 'Attendance', accent: '#2563eb',
    fields: [
      { key: 'largeGroupChurch', label: 'Large Group' },
      { key: 'children', label: 'Children' },
      { key: 'childrenWorkers', label: 'Workers' },
      { key: 'totalAttendance', label: 'Total' },
    ],
  },
  {
    id: 'financials', title: 'Financials', accent: '#059669',
    fields: [
      { key: 'donations', label: 'Donations' },
      { key: 'bookSales', label: 'Book Sales' },
      { key: 'foodDonation', label: 'Food Donation' },
      { key: 'totalFunds', label: 'Total Funds' },
    ],
  },
  {
    id: 'participation', title: 'Participation', accent: '#7c3aed',
    fields: [
      { key: 'mealsServed', label: 'Meals' },
      { key: 'blueChips', label: 'Blue Chips' },
      { key: 'teens', label: 'Teens' },
      { key: 'mensLifeIssues', label: "Men's LI" },
      { key: 'mensAddiction', label: "Men's Add" },
      { key: 'womensAddiction', label: "Women's Add" },
      { key: 'womensLifeIssues', label: "Women's LI" },
      { key: 'newBeginnings', label: 'New Beg.' },
      { key: 'totalSmallGroup', label: 'Total SG' },
    ],
  },
  {
    id: 'milestones', title: 'Milestones', accent: '#d97706',
    fields: [
      { key: 'baptisms', label: 'Baptisms' },
      { key: 'stepStudyGraduates', label: 'Graduates' },
    ],
  },
];

const ALL_FIELDS = CATEGORIES.flatMap(c => c.fields);

const isCurrencyField = (key) => {
  const k = (key || '').toLowerCase();
  return k.includes('don') || k.includes('fund') || k.includes('book') || k.includes('food');
};

const formatCell = (value, key) => {
  if (key === 'date') {
    return new Date(value).toLocaleDateString('en-US', {
      timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
    });
  }
  if (isCurrencyField(key)) return `$${(value || 0).toFixed(2)}`;
  return typeof value === 'number' ? value : value || '';
};

const formatAvg = (value, key) => {
  const v = parseFloat(value || 0);
  return isCurrencyField(key) ? `$${v.toFixed(2)}` : v.toFixed(1);
};

const formatTotal = (value, key) => {
  const v = value || 0;
  return isCurrencyField(key) ? `$${v.toFixed(2)}` : v;
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '';

const CRDashboard = () => {
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [allYears, setAllYears] = useState([]);
  const [totals, setTotals] = useState({});
  const [averages, setAverages] = useState({});
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [openSections, setOpenSections] = useState({
    attendance: true, financials: true, participation: true, milestones: true,
  });
  const [openSubs, setOpenSubs] = useState({});

  const toggleSection = (id) => setOpenSections(p => ({ ...p, [id]: !p[id] }));
  const toggleSub = (key) => setOpenSubs(p => ({ ...p, [key]: !p[key] }));

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadError('');
      setIsLoading(true);
      try {
        const { ok, data } = await get('/api/reports?limit=200');
        if (!ok) throw new Error('Failed to load reports');
        const reportList = Array.isArray(data) ? data : [];

        const sorted = [...reportList].sort((a, b) => new Date(b.date) - new Date(a.date));
        setAllReports(reportList);
        setReports(sorted.slice(0, 12));

        const years = Array.from(new Set(reportList.map(r => new Date(r.date).getFullYear()))).sort((a, b) => b - a);
        setAllYears(years);

        const yearFiltered = reportList.filter(r => new Date(r.date).getFullYear() === displayYear);
        const totalObj = {};
        const count = yearFiltered.length;
        yearFiltered.forEach(report => {
          Object.keys(report).forEach(key => {
            if (typeof report[key] === 'number') totalObj[key] = (totalObj[key] || 0) + report[key];
          });
        });
        const avgObj = {};
        Object.keys(totalObj).forEach(key => { avgObj[key] = (totalObj[key] / count).toFixed(1); });

        setTotals(totalObj);
        setAverages(avgObj);
        setLoadError('');
      } catch (err) {
        console.error('Error fetching reports:', err);
        setLoadError('Could not load reports summary. Is the backend running?');
        setReports([]); setAllReports([]); setAllYears([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [displayYear]);

  const overallAverages = useMemo(() => {
    const sums = {}; const counts = {};
    allReports.forEach(r => {
      Object.keys(r).forEach(key => {
        if (typeof r[key] === 'number') {
          sums[key] = (sums[key] || 0) + r[key];
          counts[key] = (counts[key] || 0) + 1;
        }
      });
    });
    const result = {};
    Object.keys(sums).forEach(key => { result[key] = sums[key] / counts[key]; });
    return result;
  }, [allReports]);

  const monthlyMilestones = useMemo(() => {
    const monthMap = {};
    allReports.forEach(r => {
      const d = new Date(r.date);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { month: key, baptisms: 0, stepStudyGraduates: 0 };
      monthMap[key].baptisms += (r.baptisms || 0);
      monthMap[key].stepStudyGraduates += (r.stepStudyGraduates || 0);
    });
    return Object.values(monthMap).sort((a, b) => b.month.localeCompare(a.month)).slice(0, 12);
  }, [allReports]);

  const milestoneMonthlyAvg = useMemo(() => {
    const yearMonths = monthlyMilestones.filter(m => m.month.startsWith(String(displayYear)));
    if (yearMonths.length === 0) return {};
    const result = {};
    ['baptisms', 'stepStudyGraduates'].forEach(k => {
      result[k] = yearMonths.reduce((s, m) => s + m[k], 0) / yearMonths.length;
    });
    return result;
  }, [monthlyMilestones, displayYear]);

  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split('-');
    return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getMilestoneCellHighlight = (value, key) => {
    const avg = milestoneMonthlyAvg[key];
    if (typeof value !== 'number' || avg == null || isNaN(avg)) return '';
    if (value > avg) return 'above-average';
    if (value < avg) return 'below-average';
    return '';
  };

  const { globalHighs, recentHighs } = useMemo(() => {
    const g = {}; const r = {};
    const safeMax = arr => { const v = arr.filter(x => typeof x === 'number'); return v.length ? Math.max(...v) : -Infinity; };
    ALL_FIELDS.forEach(({ key }) => {
      g[key] = safeMax(allReports.map(rep => rep[key]));
      r[key] = safeMax(reports.map(rep => rep[key]));
    });
    return { globalHighs: g, recentHighs: r };
  }, [allReports, reports]);

  const yearlyStats = useMemo(() => {
    const result = {};
    allYears.forEach(year => {
      const yrs = allReports.filter(r => new Date(r.date).getFullYear() === year);
      const count = yrs.length;
      const t = {}; const a = {};
      if (count > 0) {
        yrs.forEach(r => { Object.keys(r).forEach(k => { if (typeof r[k] === 'number') t[k] = (t[k] || 0) + r[k]; }); });
        Object.keys(t).forEach(k => { a[k] = (t[k] / count).toFixed(1); });
      }
      result[year] = { yearlyTotal: t, yearlyAvg: a };
    });
    return result;
  }, [allReports, allYears]);

  const highestByKey = useMemo(() => {
    const result = {};
    ALL_FIELDS.forEach(({ key }) => {
      let maxVal = null; let maxDate = '';
      allReports.forEach(r => {
        const v = r[key];
        if (typeof v === 'number' && (maxVal === null || v > maxVal)) { maxVal = v; maxDate = r.date; }
      });
      result[key] = { maxVal, maxDate };
    });
    return result;
  }, [allReports]);

  const getCellHighlight = (value, key) => {
    const avg = parseFloat(averages[key]);
    let cls = '';
    if (typeof value === 'number' && !isNaN(avg)) {
      if (value > avg) cls = 'above-average';
      else if (value < avg) cls = 'below-average';
    }
    if (typeof value === 'number' && value === globalHighs[key] && value === recentHighs[key]) {
      cls += ' recent-highest';
    }
    return cls.trim();
  };

  const getYoyHighlight = (yearlyAvg, key) => {
    const v = parseFloat(yearlyAvg);
    const oa = overallAverages[key];
    if (isNaN(v) || isNaN(oa)) return '';
    if (v > oa) return 'above-average';
    if (v < oa) return 'below-average';
    return '';
  };

  if (isLoading) {
    return (
      <div className="cr-dashboard">
        <PageHeader title="Reports Summary" section="gatherings" />
        <p className="dash-loading">Loading...</p>
      </div>
    );
  }

  return (
    <div className="cr-dashboard">
      <PageHeader title="Reports Summary" section="gatherings" />

      {loadError && <div className="dash-error">{loadError}</div>}

      {!loadError && (
        <>
          <div className="dash-controls">
            <div className="dash-legend">
              <span><span className="legend-box legend-green" /> Above Avg</span>
              <span><span className="legend-box legend-red" /> Below Avg</span>
              <span><span className="legend-box legend-blue" /> Record High</span>
            </div>
            <div className="dash-year-selector">
              <label htmlFor="dash-year">Year:</label>
              <select id="dash-year" value={displayYear} onChange={e => setDisplayYear(parseInt(e.target.value))}>
                {allYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {CATEGORIES.map(cat => (
            <section key={cat.id} className="dash-category">
              <button
                className={`dash-category-header dash-cat-${cat.id}`}
                onClick={() => toggleSection(cat.id)}
                style={{ '--cat-accent': cat.accent }}
              >
                <span>{openSections[cat.id] ? '▼' : '▶'} {cat.title}</span>
              </button>

              {openSections[cat.id] && (
                <div className="dash-category-body">

                  {/* Last 12 — monthly for milestones, per-entry for others */}
                  <button className="dash-sub-toggle" onClick={() => toggleSub(`${cat.id}-last12`)}>
                    {openSubs[`${cat.id}-last12`] === false ? '▶' : '▼'} {cat.id === 'milestones' ? 'Last 12 Months' : 'Last 12 Entries'}
                  </button>
                  {openSubs[`${cat.id}-last12`] !== false && cat.id === 'milestones' && (
                    <div className="dash-table-wrap">
                      <table className="dash-table">
                        <thead>
                          <tr>
                            <th className="dash-col-date">Month</th>
                            {cat.fields.map(f => (
                              <th key={f.key} className="dash-col-num">{f.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyMilestones.map(m => (
                            <tr key={m.month}>
                              <td className="dash-col-date">{formatMonth(m.month)}</td>
                              {cat.fields.map(f => (
                                <td
                                  key={f.key}
                                  className={['dash-col-num', getMilestoneCellHighlight(m[f.key], f.key)].filter(Boolean).join(' ')}
                                >
                                  {m[f.key]}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {openSubs[`${cat.id}-last12`] !== false && cat.id !== 'milestones' && (
                    <div className={`dash-table-wrap ${cat.id === 'participation' ? 'dash-table-scroll' : ''}`}>
                      <table className="dash-table">
                        <thead>
                          <tr>
                            <th className="dash-col-date">Date</th>
                            {cat.fields.map(f => (
                              <th key={f.key} className={isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num'}>
                                {f.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reports.map((report, i) => (
                            <tr key={i}>
                              <td className="dash-col-date">{formatCell(report.date, 'date')}</td>
                              {cat.fields.map(f => (
                                <td
                                  key={f.key}
                                  className={[
                                    isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num',
                                    getCellHighlight(report[f.key], f.key),
                                  ].filter(Boolean).join(' ')}
                                >
                                  {formatCell(report[f.key], f.key)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* YTD Totals & Averages */}
                  <button className="dash-sub-toggle" onClick={() => toggleSub(`${cat.id}-ytd`)}>
                    {openSubs[`${cat.id}-ytd`] === false ? '▶' : '▼'} YTD Totals & Averages ({displayYear})
                  </button>
                  {openSubs[`${cat.id}-ytd`] !== false && (
                    <div className="dash-table-wrap">
                      <table className="dash-table">
                        <thead>
                          <tr>
                            <th className="dash-col-type">Type</th>
                            {cat.fields.map(f => (
                              <th key={f.key} className={isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num'}>
                                {f.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="dash-col-type">Average</td>
                            {cat.fields.map(f => (
                              <td key={f.key} className={isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num'}>
                                {formatAvg(averages[f.key], f.key)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="dash-col-type">Total</td>
                            {cat.fields.map(f => (
                              <td key={f.key} className={isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num'}>
                                {formatTotal(totals[f.key], f.key)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Year-over-Year */}
                  <button className="dash-sub-toggle" onClick={() => toggleSub(`${cat.id}-yoy`)}>
                    {openSubs[`${cat.id}-yoy`] === false ? '▶' : '▼'} Year-over-Year Averages
                  </button>
                  {openSubs[`${cat.id}-yoy`] !== false && (
                    <div className="dash-table-wrap">
                      <table className="dash-table">
                        <thead>
                          <tr>
                            <th className="dash-col-year">Year</th>
                            {cat.fields.map(f => (
                              <th key={f.key} className={isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num'}>
                                {f.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allYears.map(year => {
                            const { yearlyAvg } = yearlyStats[year] || { yearlyAvg: {} };
                            return (
                              <tr key={year}>
                                <td className="dash-col-year">{year}</td>
                                {cat.fields.map(f => (
                                  <td
                                    key={f.key}
                                    className={[
                                      isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num',
                                      getYoyHighlight(yearlyAvg[f.key], f.key),
                                    ].filter(Boolean).join(' ')}
                                  >
                                    {formatAvg(yearlyAvg[f.key], f.key)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Records */}
                  <button className="dash-sub-toggle" onClick={() => toggleSub(`${cat.id}-records`)}>
                    {openSubs[`${cat.id}-records`] === false ? '▶' : '▼'} Records (Highest Single-Day)
                  </button>
                  {openSubs[`${cat.id}-records`] !== false && (
                    <div className="dash-table-wrap">
                      <table className="dash-table">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Highest</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cat.fields.map(f => {
                            const h = highestByKey[f.key] || {};
                            return (
                              <tr key={f.key}>
                                <td>{f.label}</td>
                                <td className={isCurrencyField(f.key) ? 'dash-col-currency' : 'dash-col-num'}>
                                  {h.maxVal !== null ? (isCurrencyField(f.key) ? `$${h.maxVal.toFixed(2)}` : h.maxVal) : '—'}
                                </td>
                                <td className="dash-col-date">{formatDate(h.maxDate)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>
          ))}
        </>
      )}
    </div>
  );
};

export default CRDashboard;
